import { useState } from "react";
import { CheckCircle2, ChevronDown, ChevronUp, RotateCcw, X, XCircle } from "lucide-react";
import { bytes } from "../lib/format";
import { cancel, clearFinished, retry, useUploads } from "../lib/upload";
import { cx } from "./ui";

export function UploadPanel() {
  const items = useUploads();
  const [collapsed, setCollapsed] = useState(false);
  if (!items.length) return null;
  const running = items.filter((i) => i.status === "queued" || i.status === "uploading");
  const failed = items.filter((i) => i.status === "failed").length;
  const total = running.reduce((n, i) => n + i.size, 0);
  const loaded = running.reduce((n, i) => n + i.loaded, 0);
  const title = running.length ? `Uploading ${running.length} file${running.length === 1 ? "" : "s"} · ${total ? Math.floor((loaded / total) * 100) : 0}%` : failed ? `${failed} upload${failed === 1 ? "" : "s"} failed` : "Uploads complete";
  return (
    <section className="fixed bottom-0 right-0 sm:bottom-4 sm:right-4 z-40 w-full sm:w-96 bg-surface border border-line sm:rounded-xl shadow-xl" aria-label="Uploads">
      <header className="flex items-center gap-2 px-4 h-12 border-b border-line">
        <span className="text-sm font-medium flex-1 tabular">{title}</span>
        <button className="p-1.5 rounded-md hover:bg-surface-2 text-muted" onClick={() => setCollapsed(!collapsed)} aria-label={collapsed ? "Expand" : "Collapse"}>
          {collapsed ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
        </button>
        {!running.length && (
          <button className="p-1.5 rounded-md hover:bg-surface-2 text-muted" onClick={clearFinished} aria-label="Close">
            <X className="size-4" />
          </button>
        )}
      </header>
      {!collapsed && (
        <ul className="max-h-72 overflow-y-auto divide-y divide-line">
          {items.map((i) => {
            const pct = i.size ? Math.floor((i.loaded / i.size) * 100) : i.status === "done" ? 100 : 0;
            return (
              <li key={i.id} className="px-4 py-2.5 text-sm">
                <div className="flex items-center gap-2">
                  <span className="flex-1 truncate" title={i.path}>
                    {i.name}
                  </span>
                  {i.status === "done" && <CheckCircle2 className="size-4 text-good shrink-0" aria-label="Done" />}
                  {i.status === "failed" && (
                    <button onClick={() => retry(i.id)} className="p-1 rounded hover:bg-surface-2 text-muted" aria-label={`Retry ${i.name}`} title="Retry">
                      <RotateCcw className="size-4" />
                    </button>
                  )}
                  {i.status === "cancelled" && <XCircle className="size-4 text-muted shrink-0" aria-label="Cancelled" />}
                  {(i.status === "queued" || i.status === "uploading") && (
                    <button onClick={() => cancel(i.id)} className="p-1 rounded hover:bg-surface-2 text-muted" aria-label={`Cancel ${i.name}`} title="Cancel">
                      <X className="size-4" />
                    </button>
                  )}
                </div>
                {(i.status === "uploading" || i.status === "queued") && (
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 h-1 rounded-full bg-surface-2 overflow-hidden">
                      <div className={cx("h-full bg-accent transition-[width]")} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-muted tabular w-28 text-right">
                      {i.status === "queued" ? "Waiting" : `${bytes(i.loaded)} / ${bytes(i.size)}`}
                    </span>
                  </div>
                )}
                {i.status === "failed" && <div className="text-xs text-bad mt-1">{i.error}</div>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
